"use client";
import { useSubPlanCode } from "@/lib/hooks/useSubPlan";
import { Box, Button, Fab, Tooltip, Typography, useTheme } from "@mui/material";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useRef, useState, useMemo, useEffect } from "react";
import GroupModal from "./GroupModal";
import GroupIcon from "./icons/GroupIcon";
import SupportIcon from "./icons/SupportIcon";

export default function FloatingButtons() {
  const theme = useTheme();
  const t = useTranslations("FloatingButtons");
  const subPlan = useSubPlanCode();
  const { status } = useSession();
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const groupButtonRef = useRef<HTMLButtonElement>(null);

  const bgColor = theme.palette.mode === "dark" ? "#F2F2F2" : "#2D3135";

  // Tooltip 样式配置
  const tooltipSx = {
    tooltip: {
      backgroundColor: "background.default",
      color: "text.secondary",
      fontSize: "0.875rem",
      lineHeight: 1.6,
      fontWeight: "var(--font-size-2xs)",
      padding: "0.5rem 0.75rem",
      border: `1px solid ${theme.palette.mode === "dark" ? "#2D3135" : "#E1E4E5"}`,
      borderRadius: "0.5rem",
      boxShadow: theme.palette.mode === "dark" ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 4px 20px rgba(0, 0, 0, 0.1)",
    },
  };

  const handleCustomerServiceClick = () => {
    // 在新标签页中打开 Telegram 群组
    window.open("https://t.me/RightPulseServiceGroup", "_blank", "noopener,noreferrer");
  };

  const handleJoinGroupClick = () => {
    // 打开加群弹窗
    setGroupModalOpen(true);
  };

  // 未登录（明确 unauthenticated）才显示；避免 loading 阶段闪烁
  const showTrialCard = status === "unauthenticated";
  // 每 20s 重播一次动画
  const [animCycle, setAnimCycle] = useState(0);
  useEffect(() => {
    if (!showTrialCard) return;
    const id = setInterval(() => setAnimCycle(c => c + 1), 20000);
    return () => clearInterval(id);
  }, [showTrialCard]);

  // 文本动画组件：英文按单词，中文按字（含全角），其它语言 fallback 按空格
  // 动画参数可调：更快掉落
  const TOKEN_ANIM_DURATION = 360; // 单个 token 动画时长 (ms)
  const TOKEN_STAGGER = 40; // 相邻 token 延迟 (ms)

  const AnimatedText = ({
    text,
    color,
    startDelay = 0,
  }: {
    text: string;
    color: "primary.main" | "text.primary";
    startDelay?: number;
  }) => {
    // 识别是否包含 CJK
    const isCJK = /[\u4e00-\u9fff]/.test(text);
    const tokens = useMemo(() => {
      if (isCJK) {
        return text.split("").filter((c) => c.trim() !== "" || c === " ");
      }
      // 英文：按照空格拆分单词并保留分隔
      const parts = text.split(/(\s+)/).filter((p) => p.length > 0);
      return parts;
    }, [text, isCJK]);

    return (
      <Box component="span" sx={{ display: "inline-block" }}>
        {tokens.map((tok, idx) => (
          <Box
            key={idx + tok}
            component="span"
            sx={{
              display: tok.trim() === "" ? "inline" : "inline-block",
              whiteSpace: tok.trim() === "" ? "pre" : "nowrap",
              opacity: 0,
              transform: "translateY(-8px)",
              animation: `rp-drop-in ${TOKEN_ANIM_DURATION}ms cubic-bezier(.25,.6,.3,1) forwards`,
              animationDelay: `${startDelay + idx * TOKEN_STAGGER}ms`,
              color
            }}>
            {tok}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: { xs: 24, sm: 80 },
        right: { xs: 16, sm: 24 },
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        alignItems: "flex-end",
      }}>
      {/* 7-day Free Membership 卡片（未登录显示） */}
      {showTrialCard && (
        <Box
          sx={{
            width: "6.5rem",
            height: "8.25rem",
            borderRadius: "0.75rem",
            p: "0.5rem 0.5rem 0.75rem",
            backgroundColor: "background.paperMedium",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "center",
            textAlign: "center",
            color: theme.palette.mode === "dark" ? "#F2F2F2" : "#F2F2F2",
          }}>
          <Box sx={{ fontSize: "1.75rem", lineHeight: 1.38 }}>🎉</Box>
          <Typography key={animCycle} sx={{ fontSize: "var(--font-size-2xs)", fontWeight: 600, lineHeight: 1.25 }}>
            {(() => {
              const line1 = t("trialCard.line1");
              const line2 = t("trialCard.line2");
              // 计算第一行 token 数（与 AnimatedText 拆分一致）
              const isCJK1 = /[\u4e00-\u9fff]/.test(line1);
              const tokensLine1 = isCJK1
                ? line1.split("").filter((c) => c.trim() !== "" || c === " ")
                : line1.split(/(\s+)/).filter((p) => p.length > 0);
              const perDelay = TOKEN_STAGGER; // ms
              const animDuration = TOKEN_ANIM_DURATION; // 单个 token 动画时长
              const totalLine1 = perDelay * (tokensLine1.length - 1) + animDuration; // 最后一个开始 + 自身时长
              return (
                <>
                  <AnimatedText key={`l1-${animCycle}`} text={line1} color="text.primary" />
                  <br />
                  <AnimatedText key={`l2-${animCycle}`} text={line2} color="primary.main" startDelay={totalLine1} />
                </>
              );
            })()}
          </Typography>
          <Button
            component={Link}
            href="/auth/signup"
            sx={{
              mt: "0.5rem",
              width: "100%",
              height: "1.6875rem",
              borderRadius: 999,
              backgroundColor: "primary.main",
              color: "#FFF",
              fontWeight: 600,
              fontSize: "var(--font-size-2xs)",
              textWrap: "nowrap",
            }}>
            {t("trialCard.cta")}
          </Button>
        </Box>
      )}
      {/* 客服按钮 */}
      <Tooltip
        title={t("customerServiceTooltip")}
        placement="left"
        slotProps={{
          tooltip: {
            sx: tooltipSx.tooltip,
          },
        }}>
        <Fab
          onClick={handleCustomerServiceClick}
          sx={{
            width: 56,
            height: 56,
            boxShadow: 3,
            backgroundColor: bgColor,
            "&:hover": {
              boxShadow: 6,
              backgroundColor: bgColor,
            },
            transition: "all 0.2s ease-in-out",
          }}>
          <SupportIcon />
        </Fab>
      </Tooltip>

      {/* 加群按钮 */}
      {subPlan && ["Visionary", "Prime"].includes(subPlan) && (
        <Tooltip
          title=""
          placement="left"
          slotProps={{
            tooltip: {
              sx: tooltipSx.tooltip,
            },
          }}>
          <Fab
            ref={groupButtonRef}
            onClick={handleJoinGroupClick}
            sx={{
              width: 56,
              height: 56,
              boxShadow: 3,
              backgroundColor: bgColor,
              "&:hover": {
                boxShadow: 6,
                backgroundColor: bgColor,
              },
              transition: "all 0.2s ease-in-out",
            }}>
            <GroupIcon />
          </Fab>
        </Tooltip>
      )}

      {/* 加群弹窗 */}
      <GroupModal open={groupModalOpen} onClose={() => setGroupModalOpen(false)} anchorEl={groupButtonRef.current} />
      {/* 动画 keyframes */}
      <style jsx global>{`
        @keyframes rp-drop-in {
          0% {
            opacity: 0;
            transform: translateY(-8px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </Box>
  );
}
